// src/pages/BecomeVolunteer.tsx
import React, { useState, FormEvent, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import DonationModal from "../../components/common/DonationModal";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/volunteer`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;


const volunteerPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Become a Volunteer | SEVAA Foundation",
  alternateName: "Volunteer with SEVAA Foundation",
  url: pageUrl,
  inLanguage: ["en-IN", "hi-IN"],
  description:
    "Become a volunteer with SEVAA Foundation and support child education, healthcare camps, food donation, women empowerment, tree plantation, elderly care and social welfare programs.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
    areaServed: [
      {
        "@type": "Country",
        name: "India",
      },
      {
        "@type": "Place",
        name: "Noida",
      },
      {
        "@type": "Place",
        name: "Jharkhand",
      },
      {
        "@type": "Place",
        name: "Chatra",
      },
      {
        "@type": "Place",
        name: "Pakur",
      },
    ],
  },
  about: [
    "NGO Volunteering",
    "Social Work",
    "Child Education Volunteer",
    "Healthcare Volunteer",
    "Food Donation Volunteer",
    "Women Empowerment Volunteer",
    "Tree Plantation Volunteer",
    "Elderly Care Volunteer",
    "CSR Volunteering",
    "Community Service",
    "Ground Operations",
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
  potentialAction: {
    "@type": "RegisterAction",
    name: "Register as Volunteer",
    target: pageUrl,
    agent: {
      "@type": "Person",
      name: "Volunteer",
    },
    object: {
      "@type": "NGO",
      "@id": `${siteUrl}/#ngo`,
      name: "SEVAA Foundation",
      url: siteUrl,
    },
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: siteUrl,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Become Volunteer",
      item: pageUrl,
    },
  ],
};

const BecomeVolunteer = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    emailAddress: "",
    interestedRole: "Field Volunteer (Drives)",
    availability: "Flexible",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://www.sevaafoundation.com/seva-backend/api/volunteer/submit.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setShowToast(true);

        setFormData({
          fullName: "",
          phoneNumber: "",
          emailAddress: "",
          interestedRole: "Field Volunteer (Drives)",
          availability: "Flexible",
        });

        setTimeout(() => {
          setShowToast(false);
        }, 2000);
      } else {
        alert(`Error: ${result.message || "Failed to submit application."}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Something went wrong. Please check if the server is running.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>Become a Volunteer | SEVAA Foundation</title>

        <meta name="title" content="Become a Volunteer | SEVAA Foundation" />

        <meta name="description" content="Become a volunteer with SEVAA Foundation and support child education, healthcare camps, food donation, women empowerment, tree plantation and elderly care." />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="Become a Volunteer | SEVAA Foundation NGO"
        />

        <meta
          property="og:description"
          content="Join SEVAA Foundation as a volunteer and support education, healthcare, food donation, women empowerment, tree plantation, elderly care and social welfare programs."
        />

        <meta property="og:image" content={defaultImage} />
        <meta
          property="og:image:alt"
          content="Become Volunteer with SEVAA Foundation NGO"
        />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Become a Volunteer | NGO Volunteer India"
        />

        <meta
          name="twitter:description"
          content="Volunteer with SEVAA Foundation and support child education, healthcare, food donation, women empowerment, tree plantation and social welfare."
        />

        <meta name="twitter:image" content={defaultImage} />
        <meta
          name="twitter:image:alt"
          content="Become a volunteer with SEVAA Foundation NGO"
        />

        <script type="application/ld+json">
          {JSON.stringify(volunteerPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-[#0a1110] text-gray-100 font-poppins relative overflow-hidden">
        {/* SUCCESS TOAST POPUP */}
        <AnimatePresence>
          {showToast && (
            <motion.div
              initial={{ opacity: 0, y: -50, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: -50, x: "-50%" }}
              role="status"
              aria-live="polite"
              className="fixed top-10 left-1/2 -translate-x-1/2 z-[100] bg-[#0f1716]/95 backdrop-blur-sm border border-emerald-500/30 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 min-w-[320px] max-w-md w-[90vw]"
            >
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <CheckCircle className="text-emerald-400" size={24} />
              </div>

              <div>
                <h4 className="text-white font-bold text-sm">
                  Thank You for Submitting!
                </h4>

                <p className="text-teal-50/70 text-xs mt-1">
                  Application received. Our team will contact you soon.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Background Decor */}
        <div
          className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none z-0"
          aria-hidden="true"
        ></div>

        {/* Hero Section */}
        <section
          aria-labelledby="volunteer-heading"
          className="relative h-[85dvh] lg:h-[75dvh] flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://cdn.sevaa.org/file/sevaa-cdn/Image_of_girl_and_love_in_face_6fcaacfb94.png')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1716]/95 via-[#0f766e]/90 to-[#0f1716]/95 z-10"></div>

          <div className="relative z-20 max-w-6xl mx-auto px-6 lg:px-8 text-center flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-8 shadow-xl"
            >
              <span className="w-2 h-2 bg-[#ffb000] rounded-full animate-pulse"></span>

              <span className="text-[#ffb000] text-[10px] md:text-xs font-black uppercase tracking-[0.2em]">
                Become part of Ground Operations
              </span>
            </motion.div>

            <motion.h1
              id="volunteer-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight mb-6"
            >
              Be the Reason Someone <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffb000] to-orange-400">
                Smiles Today.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-teal-50 text-base md:text-xl font-medium max-w-2xl mx-auto mb-8 leading-relaxed opacity-90"
            >
              Become a volunteer with SEVAA Foundation and support child
              education, healthcare camps, food donation drives, women
              empowerment, tree plantation, elderly care and community service.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="text-teal-50/80 text-sm md:text-base font-medium max-w-2xl mx-auto mb-12 leading-relaxed"
            >
              We are a family of 5,000+ passionate volunteers working on the
              ground. Whether you have a few hours or a whole weekend, your time
              is the greatest donation.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto"
            >
              <button
                onClick={scrollToForm}
                className="bg-[#ffb000] text-black font-black text-xs md:text-sm uppercase tracking-widest px-10 py-5 rounded-2xl shadow-[0_0_40px_rgba(255,176,0,0.3)] hover:bg-white hover:shadow-[0_0_40px_rgba(255,255,255,0.4)] hover:-translate-y-1 transition-all duration-300 w-full sm:w-auto"
                aria-label="Register as volunteer with SEVAA Foundation"
              >
                Register Now
              </button>

              <button
                onClick={() => navigate("/csr")}
                className="bg-transparent text-white border-2 border-white/30 font-black text-xs md:text-sm uppercase tracking-widest px-10 py-5 rounded-2xl hover:bg-white/10 hover:border-white transition-all duration-300 w-full sm:w-auto"
                aria-label="Check SEVAA Foundation CSR impact"
              >
                Check CSR Impact
              </button>
            </motion.div>
          </div>
        </section>

        {/* Volunteer Impact Grid Section */}
        <section
          aria-labelledby="volunteer-impact-heading"
          className="py-24 lg:py-32 relative z-10 px-6 lg:px-8"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16 max-w-2xl mx-auto"
            >
              <span className="text-[#ffb000] text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3 block">
                The Power of One Hour
              </span>

              <h2
                id="volunteer-impact-heading"
                className="text-3xl md:text-5xl font-black text-white leading-tight mb-5"
              >
                The Transformative Impact of{" "}
                <span className="text-emerald-400">Volunteering</span>
              </h2>

              <p className="text-teal-50/70 text-base md:text-lg">
                Beyond systemic benefits, volunteers are the face of
                compassion. It is not only about how many hours you can spare;
                it is about how deeply you care about a cause.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  num: "01",
                  label: "Force Multiplier",
                  content:
                    "NGOs often operate with limited resources. Volunteers are a force multiplier, increasing an organization’s reach, capacity and community impact.",
                },
                {
                  num: "02",
                  label: "Specialized Expertise",
                  content:
                    "Volunteers bring valuable skills in medicine, teaching, technology, finance, content, marketing or law to help communities with professional-grade support.",
                },
                {
                  num: "03",
                  label: "Last-Mile Connectors",
                  content:
                    "Local volunteers act as vital last-mile connectors by identifying real needs within the community and helping SEVAA Foundation reach the right people.",
                },
                {
                  num: "04",
                  label: "Direct Impact",
                  content:
                    "Volunteer time spent teaching a child, supporting a health camp, helping food distribution or caring for the elderly creates direct human connection.",
                },
              ].map((stat, index) => (
                <motion.article
                  key={stat.num}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#0f1716] border border-white/5 rounded-3xl p-8 relative group overflow-hidden shadow-xl"
                >
                  <div
                    className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"
                    aria-hidden="true"
                  >
                    <span className="text-8xl font-black text-white/50">
                      {stat.num}
                    </span>
                  </div>

                  <div className="relative z-10">
                    <h3 className="text-2xl font-extrabold text-white mb-4">
                      {stat.label}
                    </h3>

                    <p className="text-sm text-teal-50/60 leading-relaxed font-medium">
                      {stat.content}
                    </p>
                  </div>

                  <div className="absolute bottom-0 left-8 right-8 h-1 bg-emerald-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Ideal Volunteer Section */}
        <section
          aria-labelledby="ideal-volunteer-heading"
          className="py-24 lg:py-32 relative z-10 px-6 lg:px-8 bg-black/20"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16 max-w-2xl mx-auto"
            >
              <span className="text-emerald-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3 block">
                Changemakers on the Ground
              </span>

              <h2
                id="ideal-volunteer-heading"
                className="text-3xl md:text-5xl font-black text-white leading-tight mb-5"
              >
                Who is an{" "}
                <span className="text-[#ffb000]">Ideal Volunteer</span>?
              </h2>

              <p className="text-teal-50/70 text-base md:text-lg">
                We look for people who are passionate about creating lasting
                change in communities that need it most.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  label: "Compassion",
                  content:
                    "A genuine heart for service and commitment to reduce suffering and bring positive change.",
                },
                {
                  label: "Skill-Based",
                  content:
                    "Your unique skills have a place here. Every technical, creative or professional skill can contribute.",
                },
                {
                  label: "Local Connect",
                  content:
                    "Community insight matters. Knowledge of regional context, language and culture is vital.",
                },
                {
                  label: "Proactive Spirit",
                  content:
                    "People who can take initiative, lead small drives, inspire others and support ground operations.",
                },
              ].map((item, index) => (
                <motion.article
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#0f1716] border-2 border-dashed border-white/10 rounded-2xl px-6 py-5 flex items-center justify-between gap-4 text-center sm:text-left shadow-lg hover:border-emerald-500 transition-colors duration-300"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center shrink-0 border border-white/20"
                      aria-hidden="true"
                    >
                      <span className="text-[#ffb000] text-xl font-black">
                        ★
                      </span>
                    </div>

                    <div>
                      <h3 className="text-white font-black text-sm uppercase tracking-wider mb-1">
                        {item.label}
                      </h3>

                      <p className="text-gray-300 text-xs font-medium leading-relaxed">
                        {item.content}
                      </p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section
          aria-labelledby="volunteer-form-heading"
          className="py-24 lg:py-32 relative z-10 px-6 lg:px-8"
        >
          <div className="max-w-7xl mx-auto flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative bg-[#0f1716] border border-white/10 w-full max-w-xl p-8 md:p-10 rounded-[2rem] shadow-2xl"
            >
              <div
                className="absolute top-6 right-6 p-3 bg-white/10 rounded-xl border border-white/20"
                aria-hidden="true"
              >
                <span className="text-[#ffb000] text-lg font-black">★</span>
              </div>

              <h2
                id="volunteer-form-heading"
                className="text-3xl font-black text-white mb-2"
              >
                Volunteer Application
              </h2>

              <p className="text-teal-50/60 mb-10 text-sm font-medium">
                Join SEVAA Foundation&apos;s mission and make a real-world
                impact. Tell us a bit about yourself.
              </p>

              <form
                ref={formRef}
                className="space-y-6 text-left"
                onSubmit={handleSubmit}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label
                      htmlFor="fullName"
                      className="text-[10px] uppercase tracking-widest font-bold text-[#ffb000]"
                    >
                      Full Name
                    </label>

                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      autoComplete="name"
                      placeholder="John Doe"
                      required
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000] transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="phoneNumber"
                      className="text-[10px] uppercase tracking-widest font-bold text-[#ffb000]"
                    >
                      Phone Number
                    </label>

                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      autoComplete="tel"
                      inputMode="tel"
                      placeholder="+91 00000 00000"
                      required
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000] transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="emailAddress"
                    className="text-[10px] uppercase tracking-widest font-bold text-[#ffb000]"
                  >
                    Email Address
                  </label>

                  <input
                    type="email"
                    id="emailAddress"
                    name="emailAddress"
                    autoComplete="email"
                    placeholder="john@example.com"
                    required
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000] transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="interestedRole"
                    className="text-[10px] uppercase tracking-widest font-bold text-[#ffb000]"
                  >
                    Interested Role
                  </label>

                  <select
                    id="interestedRole"
                    name="interestedRole"
                    value={formData.interestedRole}
                    onChange={handleInputChange}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000] transition-colors appearance-none cursor-pointer"
                  >
                    <option className="bg-[#0f1716]">
                      Field Volunteer (Drives)
                    </option>
                    <option className="bg-[#0f1716]">
                      Education & Teaching
                    </option>
                    <option className="bg-[#0f1716]">
                      Content & Social Media
                    </option>
                    <option className="bg-[#0f1716]">Event Planning</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="availability"
                    className="text-[10px] uppercase tracking-widest font-bold text-[#ffb000]"
                  >
                    Availability
                  </label>

                  <select
                    id="availability"
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000] transition-colors appearance-none cursor-pointer"
                  >
                    <option className="bg-[#0f1716]">Weekends Only</option>
                    <option className="bg-[#0f1716]">Weekdays Only</option>
                    <option className="bg-[#0f1716]">Flexible</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#ffb000] text-black font-black text-sm uppercase tracking-widest py-4 rounded-xl mt-4 hover:bg-white hover:-translate-y-1 transition-all duration-300 shadow-xl disabled:opacity-50"
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            </motion.div>

            <p className="mt-12 text-sm text-teal-50/50 font-medium">or</p>

            <button
              onClick={() => navigate("/csr")}
              className="text-white hover:text-[#ffb000] text-xs uppercase tracking-widest font-black transition-colors"
              aria-label="Explore SEVAA Foundation corporate impact and CSR"
            >
              Explore Corporate Impact (CSR)
            </button>
          </div>
        </section>

        <DonationModal
          isOpen={isDonationModalOpen}
          onClose={() => setIsDonationModalOpen(false)}
        />
      </main>
    </>
  );
};

export default BecomeVolunteer;